/* eslint-disable @typescript-eslint/no-explicit-any */
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "capacitor-native-biometric";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { apiClient, isTransientRefreshFailure, kernelClient, refreshSession } from "./api";
import * as tokenStore from "./tokenStore";
import { Preferences } from "@capacitor/preferences";
import { jwtDecode } from "jwt-decode";
import { isPlatform } from "@ionic/react";
import { Device } from "@capacitor/device";
import { AUTH_CONFIG } from "../utils/constants";
import { AUTH_ENDPOINTS } from "./endpoints";
import { capitalize } from "../utils/helpers";


let googleInitialized = false;
export const isGoogleInitialized = () => googleInitialized;
export const ensureGoogleInitialized = async () => {
  if (googleInitialized) return;
  const platform = Capacitor.getPlatform();
  if (platform === "ios") {
    await SocialLogin.initialize({
      google: {
        iOSClientId: AUTH_CONFIG.google.iosClientId,
      },
    });
  } else if (platform === "android") {
    await SocialLogin.initialize({
      google: {
        webClientId: AUTH_CONFIG.google.androidWebClientId,
      },
    });
  } else {
    await SocialLogin.initialize({
      google: {
        webClientId: AUTH_CONFIG.google.webClientId,
      },
    });
  }
  googleInitialized = true;
};

// Define interfaces for JWT payloads
interface FacebookJwtPayload {
  email?: string;
  name?: string;
  picture?: string;
  [key: string]: any;
}

interface AppleJwtPayload {
  sub?: string;
  email?: string;
  email_verified?: boolean | string;
  is_private_email?: boolean | string;
  [key: string]: any;
}

/**
 * Initiates phone number authentication by sending a verification code
 * @param phone User's phone number in E.164 format
 */
/**
 * Render a Retry-After in units a person can act on.
 *
 * ⚠️ Raw seconds are fine for the 60s resend cooldown and absurd for the hourly rule:
 * `login_start` also allows only 8 per hour per identifier, and exhausting it produced
 * **"Try again in 3281 seconds."** on screen — measured, not hypothetical.
 *
 * Always rounds UP. Rounding down would invite a retry that is still inside the window,
 * which burns another rate-limit slot and repeats the error.
 */
const formatRetryAfter = (seconds: number): string => {
  if (seconds < 60) return `${Math.ceil(seconds)} seconds`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "a minute" : `${minutes} minutes`;
  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? "an hour" : `${hours} hours`;
};

/**
 * User-facing message for an auth failure. ONE mapping, used by both AuthContext and
 * VerifyLogin — they render separate error states, and duplicating the mapping is how the
 * screens end up disagreeing about what just happened.
 *
 * ⚠️ A 429 is NOT a bad code. `/auth/login/start` is rate-limited to 1/60s per identifier
 * and verify is throttled too; telling someone their code is wrong sends them to re-enter a
 * code that was fine, which burns another OTP and another rate-limit slot.
 */
export const authErrorMessage = (
  error: any,
  // ⚠️ The default fallback belongs to VERIFY, where a code has actually been entered.
  // The login-START paths must pass their own: telling someone their verification code is
  // invalid before they have been shown one is a message about a step they have not reached.
  fallback = "Invalid verification code. Please try again.",
): string => {
  const status = error?.response?.status;
  if (status === 429) {
    // Retry-After is set by the kernel alongside every RateLimitedError (app/main.py).
    // The 60s default matches the tightest live rule — login_start, 1 per 60s per
    // identifier — so a missing header still produces an honest wait rather than "now".
    const retryAfter = Number(error?.response?.headers?.["retry-after"]) || 60;
    return `Too many attempts. Try again in ${formatRetryAfter(retryAfter)}.`;
  }
  if (status === 403 && error?.response?.data?.code === "CONSENT_REQUIRED") {
    return "Please accept the Terms of Use and Privacy Policy to continue.";
  }
  if (status === 403) {
    return "This account is not active. Please contact support.";
  }
  return fallback;
};

/** Fallbacks for the paths that send a code rather than check one. */
export const SEND_CODE_FAILED = "Failed to send verification code. Please try again.";

// ── Kernel login (Slice A) ────────────────────────────────────────────────────
//
// The kernel takes ONE `identifier` (email or E.164 phone) and answers with a `session`
// handle; verify then needs the session, the SAME identifier, and the code.
//
// ⚠️ These are separate functions rather than a repoint of signInWithEmail/signInWithPhone
// on purpose. Those two serve FOUR flows — login, login-resend, phone registration, and the
// CONTACT-CHANGE resend in OtpPopup. The last is a blaze flow and stays blaze (D21 tags the
// contact-change endpoints auth-critical and migration-order-sensitive). Repointing a
// function that serves both would have sent a login OTP from a change-email screen.
//
// ⚠️ Status is HTTP status, not a `status: "ok"` body field. blaze answered 200 with
// `{status:"ok"|...}`; the kernel uses status codes and axios rejects non-2xx, so a
// truthiness check on the body is not just redundant here, it is wrong — `undefined` would
// read as failure on a perfectly good 200.

/** Storage keys for the in-flight login. Distinct from blaze's `token`/`email`/`phone`. */
const KERNEL_SESSION_KEY = "kernelLoginSession";
const KERNEL_IDENTIFIER_KEY = "kernelLoginIdentifier";
const KERNEL_METHOD_KEY = "kernelLoginMethod";

/**
 * Compose the E.164 identifier the kernel expects.
 *
 * ⚠️ Exact-string matching is load-bearing. The kernel's lazy-link compares the identifier
 * with `==` against the stored profile, so a formatting difference does not fail loudly —
 * it creates a SECOND, orphaned profile with no error and no signal (D15b). Strip everything
 * that is not a digit and prefix the country code exactly once.
 */
export const toE164 = (countryCode: string, phone: string): string => {
  const trimmed = phone.trim();

  // ⚠️ IDEMPOTENT ON PURPOSE. This used to be a blind `cc + digits`, which double-
  // applied the country code whenever the caller passed a number that already carried
  // one — Login.tsx composes `countryCode + input` and then ALSO passes countryCode.
  // The result was `+91` + `917996162528` = `+91917996162528`: 14 digits, so still legal
  // E.164 shape, so it passed validation, matched no profile on the `==` lookup, and
  // JIT-provisioned a duplicate patient record. The user logged into an empty account
  // and nothing errored (D15b). Observed live on dev 2026-08-31.
  //
  // A leading `+` means the caller already supplied a complete international number, so
  // the country code must NOT be applied again. Without the `+` we cannot tell a doubled
  // prefix from a national number that genuinely begins with those digits, so that case
  // is left alone and the kernel's country-aware validation is the backstop.
  if (trimmed.startsWith("+")) {
    return `+${trimmed.replace(/[^\d]/g, "")}`;
  }

  const digits = trimmed.replace(/[^\d]/g, "");
  const cc = countryCode.startsWith("+") ? countryCode : `+${countryCode}`;
  return `${cc}${digits}`;
};

/**
 * Start a kernel OTP login. `identifier` is an email address or an E.164 phone.
 * Returns the kernel's `{ session, delivery, method }`; the session and identifier are
 * stashed for verifyKernelLogin.
 */
export const startKernelLogin = async (identifier: string): Promise<any> => {
  const response = await kernelClient.post(AUTH_ENDPOINTS.KERNEL_LOGIN_START, {
    identifier,
  });
  const data = response?.data;
  await Preferences.set({ key: KERNEL_SESSION_KEY, value: data.session });
  await Preferences.set({ key: KERNEL_IDENTIFIER_KEY, value: identifier });
  await Preferences.set({ key: KERNEL_METHOD_KEY, value: data.method ?? "EMAIL_OTP" });
  return data;
};

/**
 * Complete a kernel OTP login.
 *
 * `termsAccepted` is passed through to the kernel, which requires it only when this call
 * completes a first-time registration (D29). The client-side gate is the device-local
 * `termsAccepted` flag; this is where that decision is recorded server-side.
 */
export const verifyKernelLogin = async (
  code: string,
  termsAccepted: boolean,
): Promise<any> => {
  const [{ value: session }, { value: identifier }, { value: method }] = await Promise.all([
    Preferences.get({ key: KERNEL_SESSION_KEY }),
    Preferences.get({ key: KERNEL_IDENTIFIER_KEY }),
    Preferences.get({ key: KERNEL_METHOD_KEY }),
  ]);
  if (!session || !identifier) {
    throw new Error("No login in progress. Request a new verification code.");
  }
  const response = await kernelClient.post(AUTH_ENDPOINTS.KERNEL_LOGIN_VERIFY, {
    session,
    identifier,
    code,
    method: method || "EMAIL_OTP",
    terms_accepted: termsAccepted,
  });
  return response?.data;
};

/** True when a kernel login has been started and not yet completed. */
export const hasKernelLoginInFlight = async (): Promise<boolean> => {
  const [{ value: session }, { value: identifier }] = await Promise.all([
    Preferences.get({ key: KERNEL_SESSION_KEY }),
    Preferences.get({ key: KERNEL_IDENTIFIER_KEY }),
  ]);
  return Boolean(session && identifier);
};

/** Clear the in-flight login handles. Safe to call when none is in progress. */
export const clearKernelLoginState = async (): Promise<void> => {
  await Promise.all([
    Preferences.remove({ key: KERNEL_SESSION_KEY }),
    Preferences.remove({ key: KERNEL_IDENTIFIER_KEY }),
    Preferences.remove({ key: KERNEL_METHOD_KEY }),
  ]);
};

/**
 * Documents the kernel still requires this profile to accept (ADR-0011).
 * Shape: `{ outstanding: [{ type, version, title }] }`.
 */
export const fetchOutstandingConsents = async (): Promise<any[]> => {
  const response = await kernelClient.get("/me/consents/outstanding");
  return response?.data?.outstanding ?? [];
};

/**
 * Accept the given document type codes. 204 on success.
 *
 * ⚠️ This is one of only FIVE routes the kernel's consent guard allows through while a
 * profile has outstanding consents (`deps.py::_CONSENT_GUARD_ALLOWLIST`) — the others being
 * `GET /me`, `GET /me/capabilities`, `GET /me/consents/outstanding` and `POST /auth/logout`.
 * That allowlist is what makes this gate resolvable from inside the app rather than a
 * lockout: the user can always sign in, see what is outstanding, accept it, and leave.
 */
export const acceptConsents = async (typeCodes: string[]): Promise<void> => {
  await kernelClient.post("/me/consents/accept", { type_codes: typeCodes });
};

/** The kernel's `/me`. Identity + consent state — deliberately thin (7 fields). */
export const fetchKernelMe = async (): Promise<any> => {
  const response = await kernelClient.get(AUTH_ENDPOINTS.KERNEL_ME);
  return response?.data;
};

/**
 * WEB, cookie-only restore (R-753, review R-748 finding 3). The patient app and the portal
 * share one host and one kernel sign-in cookie (Path=/), so someone arriving from the portal
 * can already be signed in while this app has no stored `user`. Ask the kernel `/me` quietly:
 *
 *   - 2xx → the profile (the caller then loads the blaze profile leg, as a sign-in does);
 *   - 401 → ONE silent refresh (an idle portal can leave the access cookie expired while the
 *     refresh cookie is live), then ask again;
 *   - anything else, or a second failure → `null`, and the caller shows the sign-in screen.
 *
 * ⚠️ Bypasses kernelClient's 401 interceptor on purpose (`_retry: true`): that interceptor
 * answers a failed refresh with `auth:logout`, which would run the full sign-out (a server
 * revoke) for every signed-out visitor who merely opened the app.
 */
export const probeKernelSession = async (
  refresh: () => Promise<boolean> = refreshSession,
): Promise<any | null> => {
  const ask = () => kernelClient.get(AUTH_ENDPOINTS.KERNEL_ME, { _retry: true } as any);
  try {
    return (await ask())?.data ?? null;
  } catch (error: any) {
    if (error?.response?.status !== 401) return null;
  }
  const refreshed = await refresh().catch(() => false);
  if (!refreshed) return null;
  try {
    return (await ask())?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * F-635: who does the SESSION say is signed in? App start compares this with the stored user
 * and never shows the stored one when they differ. Same quiet `/me` + one silent refresh as
 * probeKernelSession, but it tells "no session" apart from "could not ask", because native
 * must still start offline with its own stored user:
 *
 *   - `signed-in`   2xx with an id (after at most one refresh);
 *   - `signed-out`  401 and the refresh was refused (false, or a 4xx other than 429), or 401
 *                   again after it, or a 2xx without an id;
 *   - `unreachable` anything else (offline, timeout, 5xx), including a transient refresh failure.
 */
export type KernelSessionCheck =
  | { status: "signed-in"; me: any }
  | { status: "signed-out" }
  | { status: "unreachable" };

export const checkKernelSession = async (
  refresh: () => Promise<boolean> = refreshSession,
): Promise<KernelSessionCheck> => {
  const ask = () => kernelClient.get(AUTH_ENDPOINTS.KERNEL_ME, { _retry: true } as any);
  const answer = (data: any): KernelSessionCheck =>
    data?.id != null ? { status: "signed-in", me: data } : { status: "signed-out" };
  try {
    return answer((await ask())?.data);
  } catch (error: any) {
    if (error?.response?.status !== 401) return { status: "unreachable" };
  }
  let refreshed: boolean;
  try {
    refreshed = await refresh();
  } catch (error: any) {
    // R-1188 R1 (URS_3.4): a refresh that timed out, went offline, or got a 429/5xx is NOT an
    // auth verdict - the same rule as the 401 interceptor. Calling it signed-out would wipe the
    // token pair and the user's data on a cold-start blip.
    return isTransientRefreshFailure(error) ? { status: "unreachable" } : { status: "signed-out" };
  }
  if (!refreshed) return { status: "signed-out" };
  try {
    return answer((await ask())?.data);
  } catch (error: any) {
    return error?.response?.status === 401 ? { status: "signed-out" } : { status: "unreachable" };
  }
};

export const signInWithPhone = async (
  countryCode: string,
  phone: string
): Promise<any> => {
  try {
    const response: any = await apiClient.post(
      AUTH_ENDPOINTS.LOGIN_EMAIL_PHONE,
      {
        phone_country_code: countryCode,
        phone: phone,
      }
    );
    const data = response?.data;
    if (data.status === "ok") {
      Preferences.set({ key: "token", value: data.token });
      Preferences.set({ key: "phone", value: data.phone });
      return data;
    } else {
      throw new Error(data.message || "Login failed");
    }
  } catch (error) {
    console.error("Error during sign in:", error);
    throw error;
  }
};

/**
 * Initiates email authentication with password
 * @param email User's email address
 * @param password User's password
 */
export const signInWithEmail = async (email: string): Promise<any> => {
  try {
    const uniqueIdentifier = (await Device.getId()).identifier;
    const response: any = await apiClient.post(
      AUTH_ENDPOINTS.LOGIN_EMAIL_PHONE,
      {
        email,
        platform: Capacitor.getPlatform(),
        handset_id: uniqueIdentifier,
      }
    );

    const data = response?.data;
    if (data.status === "ok") {
      await Preferences.set({ key: "token", value: data.token });
      await Preferences.set({ key: "email", value: email });
      return data;
    } else {
      throw new Error(data.message || "Login failed");
    }
  } catch (error) {
    console.error("Error during email sign in:", error);
    throw error;
  }
};
/**
 * Verifies the phone number with the received code
 * @param token User's temporary token
 * @param code Verification code received via SMS
 * @returns User data if verification is successful
 */
export const verifyOtp = async (otpCode: string): Promise<any> => {
  try {
    const uniqueIdentifier = (await Device.getId()).identifier;
    const { value } = await Preferences.get({ key: "token" });
    // Use Capacitor Preferences to get the push notification token
    const { value: pushNotificationToken } = await Preferences.get({
      key: "fcmToken",
    });
    const response: any = await apiClient.post(AUTH_ENDPOINTS.LOGIN_CONFIRM, {
      token: value,
      code: otpCode,
      platform: Capacitor.getPlatform(),
      handset_id: uniqueIdentifier,
      push_notifications_token: pushNotificationToken || "",
      utc_offset: new Date().getTimezoneOffset(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (typeof response.json === "function") {
      const data = await response.json();
      if (data.status === "ok") {
        return data;
      } else {
        throw new Error(data.message || "Verification failed");
      }
    } else {
      const data = response?.data;
      if (data.status === "ok") {
        return data;
      } else {
        throw new Error(data.message || "Verification failed");
      }
    }
  } catch (error) {
    console.error("Error during verification:", error);
    throw error;
  }
};


/**
 * `signInWithSocialProvider` was REMOVED here (task 6).
 *
 * It obtained a Google/Facebook token from the native SDK and posted it to blaze's
 * `/auth/social`, which cannot work against the kernel: `/auth/social/callback` takes a
 * Cognito authorization code + PKCE verifier, not a provider token. The replacement lives in
 * `services/cognitoOauth.ts` and is wired through AuthContext.
 *
 * It also called `checkUserExists` on every social sign-in to decide "new vs returning" —
 * an account-existence oracle of exactly the kind audit R-4 removed from the kernel. The
 * kernel now answers that itself, with `new_user` on the callback response, without
 * exposing whether an address is registered to an unauthenticated caller.
 */

/**
 * Register this device with blaze after a kernel sign-in.
 *
 * ⚠️ NOT cosmetic, and not new behaviour — it RESTORES three things the auth port silently
 * removed. `logInConfirmPatient` was the only writer of all three, and it no longer runs:
 *
 *   `auth` row              `pushman.js` delivers only to rows holding a push token, so a
 *                           kernel-logged-in patient received NOTHING. Verified live: zero
 *                           `auth` rows for such a profile, while the device held a real
 *                           143-character FCM token it believed was registered.
 *   `profile_handsets` row  without it every location/AQI update matches no row and no-ops.
 *   `profile.utc_offset`    scoring is UTC-stored but bucketed in the patient's LOCAL day.
 *   + `time_zone`           a kernel-only profile sat on 'UTC' with a NULL offset, which
 *                           shifts every therapy day. This one is clinical, not cosmetic.
 *
 * ⚠️ Deliberately NOT part of the post-login `Promise.allSettled` batch, which is where D31
 * placed it: it needs `profile_id`, and `profile_id` comes FROM that batch (`GET /me`). It is
 * therefore fired after the batch and **not awaited**, which keeps D31's actual requirement —
 * that post-login costs one round-trip of latency — while removing the ordering impossibility.
 * Failures are logged, never surfaced: a device that fails to register push must not block a
 * sign-in that has already succeeded.
 */
export const registerDeviceWithBlaze = async (
  profile_id: number,
  login_source: string,
): Promise<void> => {
  try {
    const handset_id = (await Device.getId()).identifier;
    const push_notifications_token =
      (await Preferences.get({ key: "fcmToken" })).value || undefined;

    await updateHandset({
      handset_id,
      profile_id,
      login_source,
      token: "",
      // No coordinates here: this call exists to register the device, not to report
      // location. EnvironmentalDataManager still owns that, and blaze treats the AQI half
      // as optional — it no longer 500s when they are absent.
      latitude: undefined as unknown as number,
      longitude: undefined as unknown as number,
      location: undefined as unknown as string,
      platform: Capacitor.getPlatform(),
      push_notifications_token,
      // Used AS-IS, no sign flip: `getTimezoneOffset()` returns -330 for IST and the
      // column already holds -330 for existing users (checked against a live profile).
      // It is also exactly what the old login payload sent, so the convention is preserved
      // rather than reinvented.
      utc_offset: new Date().getTimezoneOffset(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch (error) {
    console.warn("Device registration with blaze failed (push may not arrive):", error);
  }
};

// ── Sign-in methods (kernel-owned) ────────────────────────────────────────────
//
// ⚠️ This REPLACES a client-side flag, not another endpoint. "Disconnect account" used to
// build a `linked_accounts` JSON with `connected: false` in the app and PUT it back through
// the generic profile update. Nothing on any server enforced it: blaze computed `canLogin`
// only inside `checkUserExists`, and blaze's own `socialAuth` never looked at it. Once D36
// removed that client-side check, disconnecting stopped having ANY effect — the button
// wrote a flag nobody read.
//
// The kernel deletes the `auth_identity` row the social login actually resolves against, so
// the method is gone by construction rather than by a flag someone must remember to check.
// See D37.

export type AuthMethod = {
  /** email_otp | sms_otp | google | apple | facebook */
  type: string;
  label: string;
  verified: boolean;
  /** false for the email_otp anchor and for the LAST remaining method. */
  removable: boolean;
};

/** The caller's sign-in methods, derived server-side from contact fields + auth_identity. */
export const fetchAuthMethods = async (): Promise<AuthMethod[]> => {
  const { data } = await kernelClient.get(AUTH_ENDPOINTS.KERNEL_AUTH_METHODS);
  return Array.isArray(data) ? data : [];
};

/**
 * Remove a sign-in method.
 *
 * ⚠️ 422 `LAST_METHOD_CANNOT_BE_REMOVED` is the case that actually matters and must reach
 * the user: the kernel refuses to leave an account with no way back in. A social signup's
 * only identity IS the social one, so for those people this is not an edge case.
 *
 * ⚠️ This does NOT block the provider. Per the D37 ruling, signing in with it again
 * re-creates the row — the kernel's email-link fallback returns the same Cognito sub, so
 * the identity comes back identical. "Disconnect" means forget-this-link, not
 * stop-this-provider.
 */
export const unlinkAuthMethod = async (methodType: string): Promise<void> => {
  await kernelClient.delete(`${AUTH_ENDPOINTS.KERNEL_AUTH_METHODS}/${methodType}`);
};

/** Maps blaze's `<provider>_account` key onto the kernel's method type. */
export const providerFromAccountKey = (accountKey: string): string =>
  accountKey.replace(/_account$/, "");

/**
 * Checks if biometric authentication is available on the device
 * @returns Object with availability status and biometric type
 */
export const checkBiometricAvailability = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await NativeBiometric.isAvailable();
      return {
        isAvailable: result.isAvailable,
        biometricType: result.biometryType,
      };
    } catch (error) {
      console.error("Error checking biometric availability:", error);
      return {
        isAvailable: false,
        biometricType: null,
      };
    }
  }

  return {
    isAvailable: false,
    biometricType: null,
  };
};

/**
 * Authenticates user with biometrics (FaceID, TouchID, etc.) or PIN as a fallback
 * @param promptMessage Message to display in the biometric prompt
 * @returns Authentication result
 */
export const authenticateWithBiometricsOrPin = async (
  promptMessage = "Authenticate to continue"
): Promise<boolean> => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Attempt biometric authentication
      await NativeBiometric.verifyIdentity({
        reason: promptMessage,
        title: "Biometric Authentication",
        // subTitle: 'Use your biometrics to securely access your account',
        // cancelTitle: 'Cancel',
      });

      return true; // Biometric authentication successful
    } catch (error) {
      console.warn("Biometric authentication failed or canceled:", error);
    }

    // Fallback to PIN authentication
    try {
      const pinResult = await promptForPin();
      return pinResult; // Return true if PIN authentication is successful
    } catch (error) {
      console.error("PIN authentication failed:", error);
      return false;
    }
  }

  return false; // Return false if not on a native platform
};

/**
 * Prompts the user for a PIN as a fallback authentication method
 * @returns True if the PIN is correct, false otherwise
 */
const promptForPin = async (): Promise<boolean> => {
  // Replace this with your custom PIN prompt logic
  const userPin = "1234"; // Example: Replace with a securely stored PIN
  const enteredPin = window.prompt("Enter your PIN:"); // Replace with a secure PIN input UI

  if (enteredPin === userPin) {
    return true; // PIN is correct
  } else {
    throw new Error("Incorrect PIN");
  }
};

/**
 * Signs out the current user
 */
/**
 * Sign out.
 *
 * ⚠️ TWO calls, deliberately — this is not a repoint from blaze to the kernel.
 *
 *   kernel  POST /auth/logout   revokes the Cognito refresh token AND tombstones the kernel
 *                               session. This is the session authority.
 *   blaze   POST /auth/logout   de-registers PUSH (`SET push_notifications_token = NULL`).
 *                               Drop this call and the device keeps receiving notifications
 *                               after sign-out — silently, since nothing errors.
 *
 * blaze's other job here — blacklisting its own token in Redis — is a no-op for a Cognito
 * token and is knowingly skipped (D19's accepted revocation gap: the kernel's session
 * revocation does not reach blaze, bounded by the 15-minute access-token TTL).
 *
 * BOTH are tolerant. A user must always be able to leave: local state is cleared by the
 * caller regardless of what either backend says. A logout that fails because the network is
 * down is a logout that stranded someone on a device they wanted to sign out of.
 */
/**
 * Sign-out is best-effort on both legs: never hold a user on a spinner to reach a backend.
 * Budgets are per-leg and deliberately small; the kernel gets the longer one because its
 * leg is the security-relevant one.
 */
const BLAZE_LOGOUT_TIMEOUT_MS = 3000;
const KERNEL_LOGOUT_TIMEOUT_MS = 5000;

const bounded = <T,>(p: Promise<T>, ms: number): Promise<T | void> =>
  Promise.race([p, new Promise<void>((resolve) => setTimeout(resolve, ms))]);

/**
 * Sign out.
 *
 * ⚠️ TWO calls, SEQUENTIAL, in this order — and the order is the whole point.
 *
 *   1. blaze  POST /auth/logout   de-registers PUSH (`SET push_notifications_token = NULL`).
 *                                 Drop this call and the device keeps receiving notifications
 *                                 after sign-out — silently, since nothing errors.
 *   2. kernel POST /auth/logout   revokes the Cognito refresh token AND tombstones the kernel
 *                                 session. This is the session authority.
 *
 * ⚠️ DO NOT run these concurrently. That was the first implementation and it was measured
 * failing on device: the access token had aged past its 15 minutes, blaze correctly rejected
 * it (`[Cognito] token rejected: jwt expired`), apiClient's 401 handler tried to refresh —
 * and the refresh token had ALREADY been revoked by the kernel leg racing alongside it. The
 * refresh failed, `clearAuthData()` fired, and the retry went out unauthenticated:
 * `No authorization token found`. Push was never de-registered. Step 2 destroys the exact
 * credential step 1 may still need, so step 1 must finish first.
 *
 * For the same reason the refresh token is read AFTER step 1: a refresh during step 1 rotates
 * it, and Cognito's rotation grace is 0 seconds, so a stale copy revokes nothing.
 *
 * blaze's other job here — blacklisting its own token in Redis — is a no-op for a Cognito
 * token and is knowingly skipped (D19's accepted revocation gap: the kernel's session
 * revocation does not reach blaze, bounded by the 15-minute access-token TTL).
 *
 * BOTH legs are tolerant. A user must always be able to leave: the caller clears local state
 * regardless of what either backend says. A sign-out that fails because the network is down
 * is a sign-out that stranded someone on a device they wanted to leave.
 */
export const signOut = async ({
  push_notifications_token,
}: {
  push_notifications_token: string;
}): Promise<void> => {
  await bounded(
    apiClient.post("/auth/logout", { push_notifications_token }).catch((err) => {
      console.warn("blaze logout (push de-registration) failed:", err);
    }),
    BLAZE_LOGOUT_TIMEOUT_MS,
  );

  // Read AFTER blaze: its 401 path may have rotated the refresh token.
  const refreshToken = await tokenStore.getRefreshToken();

  await bounded(
    kernelClient
      .post(AUTH_ENDPOINTS.KERNEL_LOGOUT, { refresh_token: refreshToken ?? undefined })
      .catch((err) => {
        console.warn("kernel logout failed:", err);
      }),
    KERNEL_LOGOUT_TIMEOUT_MS,
  );

  await clearKernelLoginState();
};

export const updateHandset = async ({
  latitude,
  longitude,
  location,
  profile_id,
  token,
  login_source,
  handset_id,
  time_zone,
  platform,
  push_notifications_token,
  utc_offset,
}: {
  latitude: number;
  longitude: number;
  location: string;
  profile_id: number | undefined;
  token: string;
  login_source: string;
  handset_id: string;
  time_zone?: string;
  platform?: string;
  push_notifications_token?: string;
  utc_offset?: number;
}): Promise<any> => {
  try {
    const response: any = await apiClient.put("/user/updateHandsetById", {
      handset_id,
      token,
      profile_id,
      login_source,
      latitude,
      longitude,
      location,
      time_zone,
      platform,
      push_notifications_token,
      utc_offset,
    });
    return response?.data;
  } catch (error) {
    console.error("Error updating handset:", error);
    throw error;
  }
};
